import {
  enumSettlementBasis,
  enumUnit,
  PurchaseLineItemType,
  SalesItemLineType,
  Unit,
} from '@rematter/frontend-lib-api-client';
import { zeroWeight } from '@rematter/modules/trade/utils/weight';
import { v4 as uuidv4 } from 'uuid';

import {
  enumLineItemKind,
  LineItemAdapter,
  NormalizedLineItem,
} from '../_types';

import { enumLoadWeightType } from '~/modules/trade/types/load';
import { PurchaseLoadLineItemRecord } from '~/modules/trade/types/purchaseLoad';

export const parsePurchaseLoadLineItem: LineItemAdapter<
  PurchaseLoadLineItemRecord,
  PurchaseLineItemType
> = {
  toNormalized: (item: PurchaseLoadLineItemRecord): NormalizedLineItem => ({
    kind: enumLineItemKind.LineItem,
    id: item.id,
    type: item.type,
    parentLineItemId: item.parentLineItemId ?? null,
    itemId: item.itemId,
    tagId: item.tagId,
    weights: {
      [enumLoadWeightType.Gross]: item.grossWeight ?? null,
      [enumLoadWeightType.Tare]: item.tareWeight ?? null,
      [enumLoadWeightType.Net]: item.netWeight ?? null,
    },
    unitPrice: item.unitPrice,
    pricingUnit: (item.pricingUnit as Unit) ?? enumUnit.Pounds,
    notes: item.notes,
    sourceLineId: item.purchaseOrderLineId,
    vehicleVin: item.vehicleVin,
    vehicleYear: item.vehicleYear,
    vehicleMake: item.vehicleMake,
    vehicleModel: item.vehicleModel,
    vehicleTitleStatus: item.vehicleTitleStatus,
    catSerialNumber: item.catSerialNumber,
    catOemCode: item.catOemCode,
    catComplianceStatus: item.catComplianceStatus,
    tareOnTruck: item.tareOnTruck ?? false,
  }),

  fromNormalized: (
    normalized: NormalizedLineItem,
    original?: PurchaseLoadLineItemRecord,
  ): PurchaseLoadLineItemRecord => ({
    id: normalized.id,
    type: normalized.type as SalesItemLineType,
    parentLineItemId:
      normalized.parentLineItemId ?? original?.parentLineItemId ?? null,
    itemId: normalized.itemId,
    tagId: normalized.tagId,
    purchaseOrderLineId:
      normalized.sourceLineId ?? original?.purchaseOrderLineId ?? null,
    grossWeight: normalized.weights?.[enumLoadWeightType.Gross] ?? null,
    tareWeight: normalized.weights?.[enumLoadWeightType.Tare] ?? null,
    netWeight: normalized.weights?.[enumLoadWeightType.Net] ?? null,
    unitPrice: normalized.unitPrice,
    estimatedPrice: original?.estimatedPrice ?? null,
    pricingUnit: normalized.pricingUnit ?? enumUnit.Pounds,
    requiresSorting: original?.requiresSorting ?? false,
    settlementBasis: original?.settlementBasis ?? enumSettlementBasis.PreSort,
    notes: normalized.notes ?? null,
    vehicleVin: normalized.vehicleVin ?? null,
    vehicleYear: normalized.vehicleYear ?? null,
    vehicleMake: normalized.vehicleMake ?? null,
    vehicleModel: normalized.vehicleModel ?? null,
    vehicleTitleStatus: normalized.vehicleTitleStatus ?? null,
    catSerialNumber: normalized.catSerialNumber ?? null,
    catOemCode: normalized.catOemCode ?? null,
    catComplianceStatus: normalized.catComplianceStatus ?? null,
    tareOnTruck: normalized.tareOnTruck ?? false,
  }),

  createEmpty: (
    type: PurchaseLineItemType,
    partial?: Partial<NormalizedLineItem>,
  ): PurchaseLoadLineItemRecord => {
    const unit: Unit = partial?.pricingUnit ?? enumUnit.Pounds;
    return {
      id: partial?.id ?? `temp-${uuidv4()}`,
      type: type as SalesItemLineType,
      itemId: partial?.itemId ?? null,
      tagId: partial?.tagId ?? null,
      purchaseOrderLineId: partial?.sourceLineId ?? null,
      grossWeight: partial?.weights?.[enumLoadWeightType.Gross] ?? zeroWeight(),
      tareWeight: partial?.weights?.[enumLoadWeightType.Tare] ?? zeroWeight(),
      netWeight: partial?.weights?.[enumLoadWeightType.Net] ?? zeroWeight(),
      unitPrice: partial?.unitPrice ?? null,
      estimatedPrice: null,
      pricingUnit: unit,
      requiresSorting: false,
      settlementBasis: enumSettlementBasis.PreSort,
      notes: partial?.notes ?? null,
      vehicleVin: partial?.vehicleVin ?? null,
      vehicleYear: partial?.vehicleYear ?? null,
      vehicleMake: partial?.vehicleMake ?? null,
      vehicleModel: partial?.vehicleModel ?? null,
      vehicleTitleStatus: partial?.vehicleTitleStatus ?? null,
      catSerialNumber: partial?.catSerialNumber ?? null,
      catOemCode: partial?.catOemCode ?? null,
      catComplianceStatus: partial?.catComplianceStatus ?? null,
      tareOnTruck: false,
    };
  },
};
